import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ACCESS_KEY = "fff_access_token";
const REFRESH_KEY = "fff_refresh_token";

export const api = axios.create({
  baseURL: `${BACKEND}/api`,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Attach Bearer token fallback (for any cross-site cookie quirks)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem(ACCESS_KEY);
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ---------- 401 auto-refresh + retry ----------
// Long compose sessions used to fail with "token expired". Instead we now
// transparently swap in a fresh access token using the 7-day refresh token
// and retry the original request. Failed refreshes (truly expired session)
// bubble the 401 up so AuthContext can redirect to /login.

let refreshInFlight = null;

async function doRefresh() {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    const stored = localStorage.getItem(REFRESH_KEY);
    const headers = stored ? { Authorization: `Bearer ${stored}` } : {};
    const res = await axios.post(
      `${BACKEND}/api/auth/refresh`,
      {},
      { withCredentials: true, headers },
    );
    if (res.data?.access_token) {
      localStorage.setItem(ACCESS_KEY, res.data.access_token);
    }
    if (res.data?.refresh_token) {
      localStorage.setItem(REFRESH_KEY, res.data.refresh_token);
    }
    return res.data;
  })().finally(() => {
    // Allow the next 401 to trigger a fresh refresh after this one settles.
    setTimeout(() => {
      refreshInFlight = null;
    }, 0);
  });
  return refreshInFlight;
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    // Only auto-retry once, and never for /auth/* endpoints (they'd loop).
    const url = (original.url || "").toString();
    const isAuthCall = url.startsWith("/auth/") || url.includes("/api/auth/");
    if (status === 401 && !original.__retried && !isAuthCall) {
      original.__retried = true;
      try {
        await doRefresh();
        const newTok = localStorage.getItem(ACCESS_KEY);
        if (newTok) {
          original.headers = original.headers || {};
          original.headers.Authorization = `Bearer ${newTok}`;
        }
        return api(original);
      } catch (refreshErr) {
        // Refresh failed — surface for debugging, then fall through with original 401.
        // Real failures here are almost always "refresh_token expired/revoked"
        // and the caller should re-auth; the intercepted 401 will bubble up
        // and AuthContext's guard will redirect to /login.
        // eslint-disable-next-line no-console
        console.error("[api] token refresh failed after 401:", refreshErr);
      }
    }
    return Promise.reject(error);
  },
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
